/**
 * /ops/backups - the control plane's own database backups and the recorded
 * restore drills (OCP-755). The page the backup alerts link to: is the newest
 * good backup fresh, did the last drill restore it, and the evidence (object,
 * SHA-256, row counts expected vs restored). Run-now for both, each with an
 * audited reason; the runs are enqueued and show up here as they progress.
 * The tenant drill (OCP-422) restores a customer's newest archive into a
 * scratch tenant and shows its phase while it runs.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface Check {
  name: string;
  expected: number | string | null;
  actual: number | string | null;
  ok: boolean;
}
interface BackupRow {
  id: string;
  status: 'running' | 'ok' | 'failed';
  trigger: string;
  started_at: string;
  finished_at: string | null;
  object_keys: string[];
  size_bytes: number | null;
  db_size_bytes: number | null;
  sha256: string | null;
  key_fingerprint: string | null;
  row_counts: Record<string, number> | null;
  duration_ms: number | null;
  error: string | null;
  actor_email: string | null;
}
interface DrillRow {
  id: string;
  kind: string;
  status: 'running' | 'passed' | 'failed';
  trigger: string;
  started_at: string;
  finished_at: string | null;
  backup_ref: string | null;
  target: string | null;
  checks: Check[];
  duration_ms: number | null;
  error: string | null;
  actor_email: string | null;
  phase: string | null;
  phase_since: string | null;
  tenant?: {
    source_subdomain: string | null;
    archive_key: string | null;
    archive_bytes: number | null;
    archive_modified_at: string | null;
    archive_sha256: string | null;
    scratch_subdomain: string | null;
    core_drill_id: string | null;
    restore_requests: number;
    warnings: string[];
    core_duration_ms: number | null;
    teardown_attempts: number;
    teardown_error: string | null;
    source_host: string | null;
    scratch_host: string | null;
    other_host: boolean | null;
  };
}
interface Overview {
  target: { configured: boolean; endpoint: string | null; bucket: string | null };
  enabled: boolean;
  schedule: string;
  drill_schedule: string;
  retention: { daily: number; monthly: number };
  max_age_hours: number;
  newest_ok_at: string | null;
  stale: boolean;
  last_passed_drill_at: string | null;
  tenant_drill: { enabled: boolean; schedule: string; last_passed_at: string | null };
  backups: BackupRow[];
  drills: DrillRow[];
}

const mb = (n: number | null) => (n == null ? '-' : `${(n / 1024 / 1024).toFixed(1)} MB`);
const secs = (ms: number | null) => (ms == null ? '-' : `${Math.round(ms / 1000)} s`);
const tone = (s: string) =>
  s === 'ok' || s === 'passed' ? 'text-green-700 dark:text-green-400' : s === 'failed' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400';

export function AdminBackupsPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'backup' | 'drill' | 'tenant-drill' | null>(null);
  const [sourceTenant, setSourceTenant] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api<Overview>('/v1/admin/ops/backups'));
    } catch (e) {
      setErr(e instanceof ApiError ? `Could not load (HTTP ${e.status}).` : 'Could not load.');
    }
  }, []);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 15_000);
    return () => clearInterval(t);
  }, [load]);

  async function run(kind: 'backup' | 'drill' | 'tenant-drill') {
    if (busy || reason.trim().length < 3) return;
    setBusy(kind);
    setMsg(null);
    try {
      if (kind === 'backup') {
        await api('/v1/admin/ops/backups/run', { method: 'POST', body: { reason: reason.trim() } });
        setMsg('Backup enqueued - it takes a few minutes and appears below.');
      } else if (kind === 'drill') {
        await api('/v1/admin/ops/restore-drills/run', { method: 'POST', body: { reason: reason.trim() } });
        setMsg('Restore drill enqueued - it appears below when it finishes.');
      } else {
        const tenantId = sourceTenant.trim();
        await api('/v1/admin/ops/restore-drills/run', { method: 'POST', body: { reason: reason.trim(), kind: 'tenant', ...(tenantId ? { tenant_id: tenantId } : {}) } });
        setMsg('Tenant restore drill enqueued - it provisions a scratch tenant and takes about an hour; its phase shows below.');
        setSourceTenant('');
      }
      setReason('');
      await load();
    } catch (e) {
      const b = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
      setMsg(b?.message ?? (e instanceof ApiError ? `Failed (HTTP ${e.status}).` : 'Failed.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-lg font-semibold">Backups &amp; restore drills</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The orboto Control database: nightly encrypted backup to the central backup host, and a monthly drill that restores the newest one into a
          scratch database and checks it. Runbook: deploy/runbooks/control-plane-backup.md. The tenant drill restores a customer&apos;s newest archive
          into a scratch tenant, compares it and deletes the scratch. Runbook: deploy/runbooks/tenant-restore-drill.md.
        </p>
      </div>
      {err && <p className="text-sm text-red-700 dark:text-red-400">{err}</p>}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-5" data-testid="backup-overview">
            <Card label="Newest good backup" value={data.newest_ok_at ? fmtDateTime(data.newest_ok_at) : 'none yet'} bad={data.stale} hint={data.stale ? `older than ${data.max_age_hours} h - alerting` : `max age ${data.max_age_hours} h`} />
            <Card label="Last passed drill" value={data.last_passed_drill_at ? fmtDateTime(data.last_passed_drill_at) : 'none yet'} bad={!data.last_passed_drill_at} hint={`schedule ${data.drill_schedule} (UTC)`} />
            <Card label="Last passed tenant drill" value={data.tenant_drill?.last_passed_at ? fmtDateTime(data.tenant_drill.last_passed_at) : 'none yet'} bad={!data.tenant_drill?.last_passed_at} hint={data.tenant_drill?.enabled ? `schedule ${data.tenant_drill.schedule} (UTC)` : 'schedule off'} />
            <Card label="Target" value={data.target.configured ? `${data.target.bucket}` : 'not configured'} bad={!data.target.configured} hint={data.target.endpoint ?? 'tenant.backup_rustfs_*'} />
            <Card label="Schedule / retention" value={data.enabled ? data.schedule : 'off'} bad={!data.enabled} hint={`${data.retention.daily} daily, ${data.retention.monthly} monthly`} />
          </div>

          <div className="flex flex-wrap items-end gap-2 rounded border border-gray-200 dark:border-gray-800 p-3">
            <label className="flex-1 text-xs text-gray-600 dark:text-gray-400">
              Reason (audited)
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. before the pg 18.4 migration" className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm" data-testid="backup-reason" />
            </label>
            <button type="button" disabled={!!busy || reason.trim().length < 3} onClick={() => void run('backup')} className="rounded bg-brand-600 px-3 py-1.5 text-sm text-white disabled:opacity-50" data-testid="run-backup">
              Back up now
            </button>
            <button type="button" disabled={!!busy || reason.trim().length < 3} onClick={() => void run('drill')} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm disabled:opacity-50" data-testid="run-drill">
              Run restore drill
            </button>
            <label className="text-xs text-gray-600 dark:text-gray-400">
              Source tenant id (optional)
              <input value={sourceTenant} onChange={(e) => setSourceTenant(e.target.value)} placeholder="oldest drill first" className="mt-0.5 block w-72 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 font-mono text-xs" data-testid="tenant-drill-source" />
            </label>
            <button type="button" disabled={!!busy || reason.trim().length < 3} onClick={() => void run('tenant-drill')} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm disabled:opacity-50" data-testid="run-tenant-drill">
              Run tenant restore drill
            </button>
            {msg && <p className="w-full text-xs text-gray-600 dark:text-gray-400">{msg}</p>}
          </div>

          <section>
            <h2 className="mb-2 text-sm font-semibold">Restore drills</h2>
            <table className="w-full text-left text-xs">
              <thead className="text-gray-500">
                <tr><th className="py-1">Started</th><th>Kind</th><th>Status</th><th>Target</th><th>Checks</th><th>Duration</th><th>By</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {data.drills.map((d) => (
                  <tr key={d.id} className="cursor-pointer align-top" onClick={() => setOpen(open === d.id ? null : d.id)} data-testid={`drill-${d.status}`}>
                    <td className="py-1">{fmtDateTime(d.started_at)}</td>
                    <td>{d.kind}</td>
                    <td className={tone(d.status)}>
                      {d.status}
                      {d.status === 'running' && d.phase && <div className="text-[11px]">{d.phase}{d.phase_since ? ` since ${fmtDateTime(d.phase_since)}` : ''}</div>}
                      {d.error && <div className="max-w-md text-[11px]">{d.error}</div>}
                    </td>
                    <td className="font-mono">
                      {d.target ?? (d.tenant?.source_subdomain ? `source ${d.tenant.source_subdomain}` : '-')}
                      {open === d.id && d.tenant && (
                        <dl className="mt-1 space-y-0.5 text-[11px]" data-testid="tenant-drill-detail">
                          <div>archive: {d.tenant.archive_key ?? '-'} ({mb(d.tenant.archive_bytes)}{d.tenant.archive_modified_at ? `, ${fmtDateTime(d.tenant.archive_modified_at)}` : ''})</div>
                          <div>sha256: {d.tenant.archive_sha256 ?? '-'}</div>
                          {d.tenant.scratch_host && (
                            <div className={d.tenant.other_host ? '' : 'text-amber-700 dark:text-amber-400'}>
                              restored on {d.tenant.scratch_host} (source on {d.tenant.source_host ?? '-'}){d.tenant.other_host ? '' : ' - same host, the pool had no other with room'}
                            </div>
                          )}
                          <div>core drill: {d.tenant.core_drill_id ?? '-'} ({d.tenant.restore_requests} request{d.tenant.restore_requests === 1 ? '' : 's'}, core {secs(d.tenant.core_duration_ms)})</div>
                          {d.tenant.warnings.length > 0 && <div className="text-amber-700 dark:text-amber-400">warnings: {d.tenant.warnings.join('; ')}</div>}
                          {d.tenant.teardown_attempts > 0 && <div className="text-red-700 dark:text-red-400">teardown attempts {d.tenant.teardown_attempts}: {d.tenant.teardown_error}</div>}
                        </dl>
                      )}
                    </td>
                    <td>
                      {d.checks.filter((c) => c.ok).length}/{d.checks.length} ok
                      {open === d.id && (
                        <ul className="mt-1 font-mono">
                          {d.checks.map((c) => (
                            <li key={c.name} className={c.ok ? '' : 'text-red-700 dark:text-red-400'}>
                              {c.name}: {String(c.actual)} {c.ok ? '=' : '!='} {String(c.expected)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td>{secs(d.duration_ms)}</td>
                    <td>{d.trigger === 'manual' ? d.actor_email : 'schedule'}</td>
                  </tr>
                ))}
                {data.drills.length === 0 && <tr><td colSpan={7} className="py-2 text-gray-500">No drill yet.</td></tr>}
              </tbody>
            </table>
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">Backups</h2>
            <table className="w-full text-left text-xs">
              <thead className="text-gray-500">
                <tr><th className="py-1">Started</th><th>Status</th><th>Object</th><th>Size</th><th>DB size</th><th>Duration</th><th>Key</th><th>By</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {data.backups.map((b) => (
                  <tr key={b.id} className="align-top" data-testid={`backup-${b.status}`}>
                    <td className="py-1">{fmtDateTime(b.started_at)}</td>
                    <td className={tone(b.status)}>
                      {b.status}
                      {b.error && <div className="max-w-md text-[11px]">{b.error}</div>}
                    </td>
                    <td className="font-mono">{b.object_keys.join(', ') || '-'}</td>
                    <td>{mb(b.size_bytes)}</td>
                    <td>{mb(b.db_size_bytes)}</td>
                    <td>{secs(b.duration_ms)}</td>
                    <td className="font-mono">{b.key_fingerprint ?? '-'}</td>
                    <td>{b.trigger === 'manual' ? b.actor_email : 'schedule'}</td>
                  </tr>
                ))}
                {data.backups.length === 0 && <tr><td colSpan={8} className="py-2 text-gray-500">No backup yet.</td></tr>}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}

function Card({ label, value, hint, bad }: { label: string; value: string; hint: string; bad: boolean }) {
  return (
    <div className={`rounded border p-3 ${bad ? 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-800'}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-medium">{value}</div>
      <div className="text-[11px] text-gray-500">{hint}</div>
    </div>
  );
}
